import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { useNotify } from '@/composables/notify'
import { useScheduleStore } from '@/stores/schedule.store'

const NO_STOCK_WARNING = 'warning.confirmNoStockServices'

// Shared "soft" confirm-allocation flow: run the confirmation; if the backend replies 409
// with the insufficient-availability warning, surface the list of resources and let the
// user confirm anyway (force). Used by every confirm entry point (project, group, task,
// timeline) so staff and equipment behave identically.
export function useAllocationConfirm() {
  const i18n = getI18n()
  const { notifyWarning } = useNotify()
  const scheduleStore = useScheduleStore()
  const noStockDialog = ref({ show: false, resources: [], onContinue: null })

  const runConfirmAsync = async(confirmFn, onSuccess) => {
    try {
      await confirmFn(false)
      await onSuccess?.()
    } catch (err) {
      const detail = err?.response?.data?.details?.[0]?.errors?.[0]
      if (err?.response?.status === 409 && detail?.code === NO_STOCK_WARNING) {
        noStockDialog.value = {
          show: true,
          resources: (detail.params ?? []).map(p => p.service).filter(Boolean),
          onContinue: async() => {
            noStockDialog.value.show = false
            await confirmFn(true)
            await onSuccess?.()
          },
        }
        return
      }
      if (detail?.code) notifyWarning(i18n.t(detail.code))
    }
  }

  const confirmAllocationAsync = (serviceId, projectId, budgetDocumentRowId, onSuccess) => runConfirmAsync(
    force => scheduleStore.confirmAllocationAsync(serviceId, projectId, [], budgetDocumentRowId, force, { skipErrorMessage: true }),
    onSuccess,
  )

  return { noStockDialog, runConfirmAsync, confirmAllocationAsync }
}
